import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Briefcase } from "lucide-react";

export default function CounselorLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u?.role === "therapist") {
        toast.success("Welcome back, doctor.");
        navigate("/therapist");
      } else if (u?.role === "admin") {
        navigate("/admin/applications");
      } else {
        toast.message("This account isn't a counselor account — sending you to your space.");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2" data-testid="page-counselor-login">
      <div className="hidden lg:flex items-center justify-center bg-mindora-ink text-mindora-bg p-16 relative overflow-hidden">
        <div className="max-w-md relative z-10">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-clay mb-6">
            <span className="inline-block w-7 h-px bg-mindora-clay mr-3 align-middle" />For clinicians
          </div>
          <h1 className="font-serif text-5xl leading-tight">
            Counselor portal.<br /><em className="italic text-mindora-clay">Sign in to your practice.</em>
          </h1>
          <p className="mt-6 text-mindora-bg/75 leading-relaxed">
            Manage your time slots, review upcoming sessions, and keep private notes on the clients you work with.
          </p>
          <div className="mt-10 border-t border-mindora-bg/20 pt-6 text-sm text-mindora-bg/70">
            Not on Mindora yet?{" "}
            <Link to="/directory/apply" className="text-mindora-clay underline hover:no-underline" data-testid="counselor-login-apply-link">
              Apply to practice with us
            </Link>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={handle} className="w-full max-w-md" data-testid="counselor-login-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-mindora-red">
            <Briefcase size={14} /> Counselor sign-in
          </div>
          <h2 className="font-serif text-4xl text-mindora-ink mt-3">Welcome back.</h2>
          <p className="text-sm text-mindora-muted mt-2">Use the credentials we sent when your application was approved.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-mindora-ink/80 text-xs uppercase tracking-wider">Practice email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="counselor-login-email"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-mindora-ink/80 text-xs uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="counselor-login-password"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="counselor-login-submit"
            className="mt-7 w-full btn-primary rounded-full h-11"
          >
            {busy ? "Signing in…" : "Sign in to portal"}
          </Button>

          <p className="mt-6 text-sm text-mindora-muted text-center">
            Looking for the client sign-in?{" "}
            <Link to="/login" className="text-mindora-red hover:underline" data-testid="counselor-to-user-login">
              That's over here
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
